import React, { createContext, useContext, ReactNode, useState, useEffect, useRef, useMemo } from 'react';
import { FF_NIGHT_MODE } from '@shared/utils/feature-flag';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = 'theme';
const DATA_THEME_ATTRIBUTE = 'data-mantine-color-scheme';

const isValidStoredTheme = (theme: string | null): theme is Theme =>
  theme === 'light' || theme === 'dark';

const getSystemTheme = (): Theme => {
  const canDetect = typeof window !== 'undefined' && window.matchMedia;
  return canDetect && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialTheme = (): Theme => {
  if (!FF_NIGHT_MODE) return 'light';

  const urlParams = new URLSearchParams(window.location.search);
  const urlTheme = urlParams.get('theme');
  if (isValidStoredTheme(urlTheme)) return urlTheme;

  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  return isValidStoredTheme(storedTheme) ? storedTheme : getSystemTheme();
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const initialTheme = useMemo(() => getInitialTheme(), []);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const hasUserPreference = useRef<boolean>(!!localStorage.getItem(THEME_STORAGE_KEY));
  const mediaQueryRef = useRef<MediaQueryList | null>(null);

  const applyThemeToDOM = (t: Theme) => {
    document.documentElement.setAttribute(DATA_THEME_ATTRIBUTE, t);
    document.documentElement.setAttribute('data-theme', t);
    if (t === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  useEffect(() => {
    applyThemeToDOM(initialTheme);
  }, [initialTheme]);

  useEffect(() => {
    applyThemeToDOM(theme);
    if (hasUserPreference.current) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  useEffect(() => {
    if (!FF_NIGHT_MODE) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQueryRef.current = mediaQuery;

    const handleChange = (e: MediaQueryListEvent) => {
      if (hasUserPreference.current) return;
      const newTheme: Theme = e.matches ? 'dark' : 'light';
      setTheme(newTheme);
      applyThemeToDOM(newTheme);
    };

    const subscribe = mediaQuery.addEventListener ?? mediaQuery.addListener.bind(mediaQuery);
    const unsubscribe = mediaQuery.removeEventListener ?? mediaQuery.removeListener.bind(mediaQuery);
    subscribe('change', handleChange);

    return () => { unsubscribe('change', handleChange); };
  }, []);

  const toggleTheme = () => {
    if (!FF_NIGHT_MODE) return;
    hasUserPreference.current = true;
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const contextValue: ThemeContextType = {
    theme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;

